//@flow
import React, {useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Chip,
  MenuItem,
} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import {Add} from '@material-ui/icons';
import TriggerCard from './TriggerCard';
import {useChatTriggers, useChatTriggerActions} from '../utils';
import {colors} from '../../../../assets/styles/colors';
import Pagination from '@material-ui/lab/Pagination';
import {getPaginationCount} from '../../../../utils/pagination';
import {TRIGGER_TYPE_OPTIONS} from './TriggerForm/const';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useTierRestrictionActions} from '../../../TierRestriction/utils';
import {Alert} from '@material-ui/lab';
import UpgradeIcon from '../../../../elements/icons/UpgradeIcon';
import Toast from '../../../../elements/Toast';
import type {ListTriggersParams} from '../api';
import type {TriggerType} from '../../../../entities';
import {isCustomerInactivityTrigger} from '../utils/triggerConfig';
import {useMultichatFunnels} from '../../funnels/utils';
import {useConversationTags} from '../../chat/utils/conversationTag';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 'calc(100vh - 100px)',
    minWidth: '100%',
    paddingTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  triggersContainer: {
    padding: '0',
    background: 'transparent',
    boxShadow: 'none',
    height: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  buttonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    fontSize: '.85rem',
    '& .icon': {
      display: 'none',
    },
    [theme.breakpoints.up('lg')]: {
      '& .icon': {
        display: 'flex',
      },
    },
  },
  emptyTrigger: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '1rem',
    maxWidth: '450px',
    margin: 'auto',
    padding: '2rem',
    background: '#fff',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  emptyTriggerText: {
    color: colors.grey700,
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: '1.25',
  },
  addTriggerButton: {
    padding: '.5rem 2rem',
    fontSize: '1rem',
    borderRadius: '12px',
  },
  pagination: {
    width: '100%',
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'center',
  },
  upgradeButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    fontWeight: 600,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
  },
  upgradeButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'center',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  filterSelects: {
    display: 'flex',
    alignItems: 'center',
    gap: '.75rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  filterSelect: {
    minWidth: 200,
  },
  filterChip: {
    fontWeight: 600,
    cursor: 'pointer',
  },
  filterChipSelected: {
    backgroundColor: colors.purple3,
    color: '#fff',
    '&:hover': {
      backgroundColor: colors.purple3,
    },
  },
}));

export default function Triggers() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [triggerTypeFilter, setTriggerTypeFilter] = useState<TriggerType | ''>(
    ''
  );
  const [funnelIdFilter, setFunnelIdFilter] = useState('');
  const [tagIdFilter, setTagIdFilter] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const {funnels} = useMultichatFunnels();
  const {conversationTags} = useConversationTags();

  useEffect(() => {
    if (location.state?.added) {
      setSuccessMessage('Respuesta automatizada creada correctamente');
      history.replace({...location, state: undefined});
    } else if (location.state?.edited) {
      setSuccessMessage('Respuesta automatizada actualizada correctamente');
      history.replace({...location, state: undefined});
    }
  }, [history, location]);

  const listParams: ListTriggersParams | void = useMemo(() => {
    const params: ListTriggersParams = {};

    if (triggerTypeFilter) {
      params.trigger_type = triggerTypeFilter;
    }

    // Funnel/tag list filters only apply to inactivity triggers.
    if (isCustomerInactivityTrigger(triggerTypeFilter)) {
      if (funnelIdFilter) {
        params.funnel_id = funnelIdFilter;
      }
      if (tagIdFilter) {
        params.tag_id = tagIdFilter;
      }
    }

    return Object.keys(params).length > 0 ? params : undefined;
  }, [triggerTypeFilter, funnelIdFilter, tagIdFilter]);

  const showInactivityListFilters = isCustomerInactivityTrigger(
    triggerTypeFilter
  );

  const handleTriggerTypeFilterChange = (nextType: TriggerType | '') => {
    setTriggerTypeFilter(nextType);
    if (!isCustomerInactivityTrigger(nextType)) {
      setFunnelIdFilter('');
      setTagIdFilter('');
    }
  };

  const {triggers, loading, pagination} = useChatTriggers(listParams);

  const {
    toggleTriggerEnabled,
    changeTriggerPagination,
    deleteTrigger,
  } = useChatTriggerActions();

  const hasChatTriggersFeature = useIsFeatureInTier('TIENDACHAT_TRIGGERS');
  const {showTierRestrictionModal} = useTierRestrictionActions();

  const handleButtonClick = e => {
    if (hasChatTriggersFeature) return;
    e.preventDefault();
    showTierRestrictionModal();
  };

  return (
    <div className={classes.root}>
      <Toast
        severity="success"
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      >
        {successMessage}
      </Toast>
      <Alert severity="info">
        Las respuestas automatizadas configuradas solo funcionarán cuando el
        chat esté cerrado.
      </Alert>
      {!hasChatTriggersFeature && (
        <CustomButton className={classes.upgradeButton} to="/tiers-list">
          <div className={classes.upgradeButtonLabel}>
            <span>Mejora tu plan para acceder a esta funcionalidad</span>
            <UpgradeIcon width={18} height={18} />
          </div>
        </CustomButton>
      )}

      {(triggers.length > 0 ||
        triggerTypeFilter ||
        funnelIdFilter ||
        tagIdFilter) && (
        <div className={classes.triggersContainer}>
          <div className={classes.topBar}>
            <Typography variant="h5" style={{fontWeight: 700}}>
              Respuestas Automáticas
            </Typography>
            <CustomButton
              variant="contained"
              component={Link}
              onClick={handleButtonClick}
              disabled={!hasChatTriggersFeature}
              className={classes.addTriggerButton}
              to="/chat/triggers/new"
            >
              <span className={classes.buttonLabel}>
                <Add fontSize="small" className="icon" />
                <span>Crear nuevo</span>
              </span>
            </CustomButton>
          </div>

          <div className={classes.filtersRow}>
            <Chip
              label="Todos"
              clickable
              className={`${classes.filterChip} ${
                triggerTypeFilter === '' ? classes.filterChipSelected : ''
              }`}
              color={triggerTypeFilter === '' ? 'primary' : 'default'}
              onClick={() => handleTriggerTypeFilterChange('')}
            />
            {TRIGGER_TYPE_OPTIONS.map(option => (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                className={`${classes.filterChip} ${
                  triggerTypeFilter === option.value
                    ? classes.filterChipSelected
                    : ''
                }`}
                color={
                  triggerTypeFilter === option.value ? 'primary' : 'default'
                }
                onClick={() => handleTriggerTypeFilterChange(option.value)}
              />
            ))}
          </div>

          {showInactivityListFilters && (
            <div className={classes.filterSelects}>
              <CustomSelect
                className={classes.filterSelect}
                placeholder="Filtrar por embudo"
                value={funnelIdFilter}
                displayEmpty
                onChange={event => setFunnelIdFilter(event.target.value)}
              >
                <MenuItem value="">Todos los embudos</MenuItem>
                {funnels.map(funnel => (
                  <MenuItem key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </MenuItem>
                ))}
              </CustomSelect>

              <CustomSelect
                className={classes.filterSelect}
                placeholder="Filtrar por etiqueta"
                value={tagIdFilter}
                displayEmpty
                onChange={event => setTagIdFilter(event.target.value)}
              >
                <MenuItem value="">Todas las etiquetas</MenuItem>
                {conversationTags.map(tag => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.tag}
                  </MenuItem>
                ))}
              </CustomSelect>
            </div>
          )}

          {triggers.length > 0 ? (
            <Grid container spacing={2}>
              {triggers.map(trigger => (
                <Grid item xs={12} md={6} lg={4} key={trigger.id}>
                  <TriggerCard
                    trigger={trigger}
                    onEdit={() => {
                      history.push(`/chat/triggers/${trigger.id}`);
                    }}
                    onDelete={() => {
                      void deleteTrigger(trigger.id);
                    }}
                    onToggleStatus={() => {
                      void toggleTriggerEnabled(trigger.id, !trigger.enabled);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            !loading && (
              <Paper className={classes.emptyTrigger}>
                <Typography
                  variant="h6"
                  align="center"
                  className={classes.emptyTriggerText}
                >
                  No hay triggers con estos filtros
                </Typography>
                <Typography align="center">
                  Prueba cambiando el tipo, embudo o etiqueta de conversación.
                </Typography>
              </Paper>
            )
          )}

          {pagination && triggers.length > 0 && (
            <Pagination
              count={getPaginationCount(pagination)}
              page={pagination.page + 1}
              className={classes.pagination}
              color="primary"
              onChange={(e, page) => changeTriggerPagination(page, listParams)}
            />
          )}
        </div>
      )}

      {triggers.length === 0 &&
        !loading &&
        !triggerTypeFilter &&
        !funnelIdFilter &&
        !tagIdFilter && (
          <div className={classes.triggersContainer}>
            <Paper className={classes.emptyTrigger}>
              <Typography
                variant="h6"
                align="center"
                className={classes.emptyTriggerText}
              >
                Aún no tienes ningún trigger
              </Typography>

              <Typography align="center">
                Impulsa la comunicación instantánea con tus clientes enviando
                mensajes automáticos en respuesta a sus consultas y acciones.
              </Typography>

              <CustomButton
                variant="contained"
                component={Link}
                onClick={handleButtonClick}
                disabled={!hasChatTriggersFeature}
                to="/chat/triggers/new"
                className={classes.addTriggerButton}
              >
                <span className={classes.buttonLabel}>
                  <Add fontSize="small" className="icon" />
                  <span>Crear Trigger</span>
                </span>
              </CustomButton>
            </Paper>
          </div>
        )}
    </div>
  );
}
